import * as Sentry from "@sentry/browser";
import Supervisor from "./worker/supervisor";
import extensionStorage from "./extensionStorage";
import BarrelType from "../model/BarrelType";
import {
    serializeData,
    serializeScreen,
    serializeComponent,
    serializeVersion,
    serializeVariantSection,
    findLayerIndexPath
} from "../utils/extensions";

const extensionMethods = [
    "load",
    "layer",
    "screen",
    "component",
    "comment",
    "colors",
    "textStyles",
    "spacing",
    "exportColors",
    "exportTextStyles",
    "exportSpacing",
    // Below will be deprecated
    "styleguideColors",
    "styleguideTextStyles",
    "exportStyleguideColors",
    "exportStyleguideTextStyles"
];

// This method is created to remove the field called "properties" inside component object.
// We are removing this field just because there is a property name clash. Within the context of variant components
// feature, we manually added a field called "properties" to component data(See serializeComponent method in
// extensions.ts). Since we let third party apps and zeplin users to create their own extension, renaming or removing
// manually added field could cause an unwanted or unexpected outcomes. Instead of renaming/removing manually added
// property, since there are no use case of "properties" field inside component model inside extensions, we decided
// not to pass this field from component objects to prevent naming clash.
function clearComponentProperties(barrelObject) {
    if (!barrelObject) {
        return barrelObject;
    }

    const clearedObject = { ...barrelObject };

    if (clearedObject && clearedObject.components) {
        clearedObject.components = clearedObject.components.map(({ properties: _, ...others }) => ({ ...others }));
    }

    if (clearedObject && clearedObject.componentSections && clearedObject.componentSections.length > 0) {
        clearedObject.componentSections =
            clearedObject.componentSections.map(section => clearComponentProperties(section));
    }

    if (clearedObject && clearedObject.styleguide) {
        clearedObject.styleguide = clearComponentProperties(clearedObject.styleguide);
    }

    if (clearedObject.parent) {
        clearedObject.parent = clearComponentProperties(barrelObject.parent);
    }

    return clearedObject;
}

class WebappExtension {
    constructor(data) {
        const {
            _id,
            name,
            description,
            manifestURL,
            default: isDefault,
            users,
            manifest,
            local,
            options = {},
            storage,
            supervisor,
            barrelType = BarrelType.PROJECT,
            loaded = false,
            methods = []
        } = data;

        this._id = _id;
        this.name = name;
        this.description = description;
        this.manifestURL = manifestURL;
        this.default = isDefault;
        this.users = users;
        this.manifest = manifest;
        this.local = local;
        this.options = { ...options };
        this.storage = storage;
        this.supervisor = supervisor;
        this.barrelType = barrelType;
        this.loaded = loaded;
        this.methods = methods;
    }

    static async loadManifest(url) {
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`Cannot load extension manifest: ${url}`);
        }

        return response.json();
    }

    static async createLocal(url) {
        const manifestURL = url.endsWith("manifest.json") ? url : `${url.replace(/\/$/, "")}/manifest.json`;

        const manifest = await WebappExtension.loadManifest(manifestURL);
        const { name, description } = manifest;

        return new WebappExtension({
            _id: name,
            name,
            description,
            manifestURL,
            manifest,
            default: false,
            users: [{
                user: {
                    displayName: Zeplin.user.displayName || Zeplin.user.username
                },
                role: "owner"
            }],
            local: true
        });
    }

    workerLogger = (error, extra = {}) => {
        if (this.local) {
            /* eslint-disable no-console */
            console.error(`Zeplin extension error (${this.name}):`, error);
            console.error("Data:", extra);
            /* eslint-enable no-console */
        } else {
            Sentry.withScope(scope => {
                scope.setExtra("extension", {
                    name: this.name,
                    id: this._id
                });

                Object.entries(extra).forEach(([key, extraVal]) => scope.setExtra(key, extraVal));

                Sentry.captureException(error);
            });
        }
    }

    initWorker(moduleURL) {
        this.supervisor = new Supervisor(new Worker(Zeplin.config.extensionWorkerPath), {
            errorLogger: this.workerLogger,
            methods: extensionMethods
        });

        return this.supervisor.load(moduleURL);
    }

    async initOptions(eid, pid, optionsSchema) {
        const defaultOptions = Object.fromEntries(optionsSchema.map(({ id, default: isDefault }) => [id, isDefault]));
        this.storage = extensionStorage.create(eid, pid, defaultOptions);
        this.options = await this.storage.getAll();
    }

    /**
     *
     * @param {import("../foundation/model/project").default} barrel
     * @param {("project"|"styleguide")} type
     */
    async load(barrelId, type = BarrelType.PROJECT) {
        this.barrelType = type;

        try {
            if (!this.local) {
                this.manifest = await WebappExtension.loadManifest(this.manifestURL);
            }

            await this.initOptions(this._id, barrelId, this.optionsSchema);
            const workerURL = new URL(this.manifest.moduleURL, this.manifestURL).toString();
            const methods = await this.initWorker(workerURL);
            this.loaded = true;
            this.methods = methods;
        } catch (_) {
            // No-op
        }

        return this;
    }

    reload() {
        if (!this.loaded) {
            return;
        }

        const extensionURL = new URL(this.manifest.moduleURL, this.manifestURL).toString();

        return this.supervisor.load(extensionURL);
    }

    get id() {
        return this._id;
    }

    get optionsSchema() {
        return this.manifest ? (this.manifest.options || []) : [];
    }

    get author() {
        const { user = { displayName: "Anonymous" } } = this.users.find(({ role }) => role === "owner");

        return {
            displayName: user.displayName,
            url: this.manifest && this.manifest.author && this.manifest.author.url
        };
    }

    get repository() {
        return this.manifest && this.manifest.repository;
    }

    terminate() {
        if (this.supervisor) {
            this.supervisor.terminate();
        }

        if (this.local) {
            extensionStorage.clear(this._id);
        }
    }

    setOption(name, value) {
        this.options[name] = value;
        this.storage.set(name, value);
    }

    getOption(name) {
        return this.options[name];
    }

    getContext(barrelObject) {
        const inProjectContext = this.barrelType === BarrelType.PROJECT;
        const data = {
            options: this.options
        };

        const clearedObject = clearComponentProperties(barrelObject);

        if (inProjectContext) {
            data.project = clearedObject;
        } else {
            data.styleguide = clearedObject;
        }

        return {
            model: "Context",
            data
        };
    }

    getDeprecatedContext(barrelObject) {
        const clearedObject = clearComponentProperties(barrelObject);

        return {
            model: "Context",
            data: {
                project: clearedObject,
                options: this.options
            }
        };
    }

    getPrimaryOptions() {
        return this.optionsSchema.filter(option => option.primary)
            .map(option => ({ ...option, value: this.options[option.id] }));
    }

    hasMethod(methodName) {
        return this.methods.includes(methodName);
    }

    styleguideColors(barrelObject) {
        if (this.hasMethod("colors")) {
            return this.supervisor.colors(this.getContext(barrelObject));
        }

        return this.supervisor.styleguideColors(this.getDeprecatedContext(barrelObject), barrelObject.colors.map(color => serializeData(color, "Color")));
    }

    styleguideTextStyles(barrelObject) {
        if (this.hasMethod("textStyles")) {
            return this.supervisor.textStyles(this.getContext(barrelObject));
        }

        return this.supervisor.styleguideTextStyles(this.getDeprecatedContext(barrelObject), barrelObject.textStyles.map(textStyle => serializeData(textStyle, "Font")));
    }

    styleguideSpacings(barrelObject) {
        return this.supervisor.spacing(this.getContext(barrelObject));
    }

    layer(barrelObject, selectedLayer, selectedVersion) {
        const idxPath = findLayerIndexPath(selectedVersion.snapshot, selectedLayer.id);

        if (!idxPath) {
            return Promise.resolve(null);
        }

        return this.supervisor.layer(this.getContext(barrelObject), idxPath, serializeVersion(selectedVersion));
    }

    screen(barrelObject, selectedVersion, selectedScreen) {
        return this.supervisor.screen(this.getContext(barrelObject), serializeVersion(selectedVersion), serializeScreen(selectedScreen));
    }

    component(barrelObject, selectedVersion, selectedComponent, variantSection) {
        if (variantSection) {
            const serializedVariantSection = serializeVariantSection(variantSection);
            const selectedComponentIndex = serializedVariantSection.data.components.findIndex(c => c.id === selectedComponent._id);

            return this.supervisor.component(this.getContext(barrelObject), serializeVersion(selectedVersion), selectedComponentIndex, serializedVariantSection);
        }

        return this.supervisor.component(this.getContext(barrelObject), serializeVersion(selectedVersion), serializeComponent(selectedComponent));
    }

    comment(barrelObject, text) {
        return this.supervisor.comment(this.getContext(barrelObject), text);
    }

    exportStyleguideColors(barrelObject) {
        if (this.hasMethod("exportColors")) {
            return this.supervisor.exportColors(this.getContext(barrelObject));
        }

        return this.supervisor.exportStyleguideColors(this.getDeprecatedContext(barrelObject), barrelObject.colors.map(color => serializeData(color, "Color")));
    }

    exportStyleguideTextStyles(barrelObject) {
        if (this.hasMethod("exportTextStyles")) {
            return this.supervisor.exportTextStyles(this.getContext(barrelObject));
        }

        return this.supervisor.exportStyleguideTextStyles(this.getDeprecatedContext(barrelObject), barrelObject.textStyles.map(textStyle => serializeData(textStyle, "Font")));
    }

    exportStyleguideSpacings(barrelObject) {
        return this.supervisor.exportSpacing(this.getContext(barrelObject));
    }
}

export { WebappExtension };
